/**
 * Copyright (c) 2026, CheckPlus Limited
 */
/**
 * Main Routes
 */
import React from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import { PLATFORM } from "./config";

import PublicRoute from "./PublicRoute";
import HomePageRoute from "./HomePageRoute";
import StripeRoute from "./StripeRoute";
import ProtectedOrgRoute from "./ProtectedOrgRoute";
import AdminLoginRoute from "./AdminLoginRoute";
import AdminProtectedRoute from "./AdminProtectedRoute";
import OrgLoginRoute from "./OrgLoginRoute";

/* Homepages in English */
import En from "./app/En/En";
import EnVideo from "./app/EnVideo/EnVideo";
import EnTeam from "./app/EnTeam/EnTeam";
import EnEdward from "./app/EnEdward/EnEdward";
import EnRix from "./app/EnRix/EnRix";
import EnWhyCheckPlus from "./app/EnWhyCheckPlus/EnWhyCheckPlus";
import EnContact from "./app/EnContact/EnContact";
import EnFaq from "./app/EnFaq/EnFaq";
import EnPricing from "./app/EnPricing/EnPricing";
import EnSecurity from "./app/EnSecurity/EnSecurity";
import EnResponsibility from "./app/EnResponsibility/EnResponsibility";
import EnSupportHK from "./app/EnSupportHK/EnSupportHK";
import EnStripe from "./app/EnStripe/EnStripe";
import EnPrivacy from "./app/EnPrivacy/EnPrivacy";
import EnPrivacyPolicy from "./app/EnPrivacyPolicy/EnPrivacyPolicy";
import EnTerms from "./app/EnTerms/EnTerms";

/* Homepages in Traditional Chinese */
import Zh from "./app/Zh/Zh";
import ZhVideo from "./app/ZhVideo/ZhVideo";
import ZhTeam from "./app/ZhTeam/ZhTeam";
import ZhEdward from "./app/ZhEdward/ZhEdward";
import ZhRix from "./app/ZhRix/ZhRix";
import ZhWhyCheckPlus from "./app/ZhWhyCheckPlus/ZhWhyCheckPlus";
import ZhContact from "./app/ZhContact/ZhContact";
import ZhFaq from "./app/ZhFaq/ZhFaq";
import ZhPricing from "./app/ZhPricing/ZhPricing";
import ZhSecurity from "./app/ZhSecurity/ZhSecurity";
import ZhResponsibility from "./app/ZhResponsibility/ZhResponsibility";
import ZhSupportHK from "./app/ZhSupportHK/ZhSupportHK";
import ZhStripe from "./app/ZhStripe/ZhStripe";
import ZhPrivacy from "./app/ZhPrivacy/ZhPrivacy";
import ZhPrivacyPolicy from "./app/ZhPrivacyPolicy/ZhPrivacyPolicy";
import ZhTerms from "./app/ZhTerms/ZhTerms";

/* Homepages in Simplified Chinese */
import Cn from "./app/Cn/Cn";
import CnVideo from "./app/CnVideo/CnVideo";
import CnTeam from "./app/CnTeam/CnTeam";
import CnEdward from "./app/CnEdward/CnEdward";
import CnRix from "./app/CnRix/CnRix";
import CnWhyCheckPlus from "./app/CnWhyCheckPlus/CnWhyCheckPlus";
import CnContact from "./app/CnContact/CnContact";
import CnFaq from "./app/CnFaq/CnFaq";
import CnPricing from "./app/CnPricing/CnPricing";
import CnSecurity from "./app/CnSecurity/CnSecurity";
import CnResponsibility from "./app/CnResponsibility/CnResponsibility";
import CnSupportHK from "./app/CnSupportHK/CnSupportHK";
import CnStripe from "./app/CnStripe/CnStripe";
import CnPrivacy from "./app/CnPrivacy/CnPrivacy";
import CnPrivacyPolicy from "./app/CnPrivacyPolicy/CnPrivacyPolicy";
import CnTerms from "./app/CnTerms/CnTerms";

/* Organization Account */
import OrgLogin from "./app/OrgLogin/OrgLogin";
import OrgLoginPassword from "./app/OrgLoginPassword/OrgLoginPassword";
import OrgSignup from "./app/OrgSignup/OrgSignup";
import OrgAgreement from "./app/OrgAgreement/OrgAgreement";
import OrgRegisterInfo from "./app/OrgRegisterInfo/OrgRegisterInfo";
import OrgConfirm from "./app/OrgConfirm/OrgConfirm";
import OrgForgotPassword from "./app/OrgForgotPassword/OrgForgotPassword";
import OrgForgotPasswordConfirm from "./app/OrgForgotPasswordConfirm/OrgForgotPasswordConfirm";
import UsernameConfirm from "./app/UsernameConfirm/UsernameConfirm";
import OrgSetup from "./app/OrgSetup/OrgSetup";
import AccountOverview from "./app/AccountOverview/AccountOverview";
import CreateGoal from "./app/CreateGoal/CreateGoal";
import AddOfflinePayments from "./app/AddOfflinePayments/AddOfflinePayments";
import OrgSettings from "./app/OrgSettings/OrgSettings";
import OrgBankAdd from "./app/OrgBankAdd/OrgBankAdd";
import OrgBankInfo from "./app/OrgBankInfo/OrgBankInfo";
import OrgDecline from "./app/OrgDecline/OrgDecline";
import PayoutSuccess from "./app/PayoutSuccess/PayoutSuccess";
import EmailVerified from "./app/EmailVerified/EmailVerified";
import DonorEmailVerified from "./app/DonorEmailVerified/DonorEmailVerified";
import ResetEmail from "./app/ResetEmail/ResetEmail";
import EmailResetPassword from "./app/EmailResetPassword/EmailResetPassword";
import OrgPlanSelection from "./app/OrgPlanSelection/OrgPlanSelection"; // Import the plan selection component
import OrgPlanBuilder from "./app/OrgPlanBuilder/OrgPlanBuilder"; // Import the plan builder component

/* Plug Marketplace */
import Marketplace from "./app/Marketplace/Marketplace";

/* Plug marketing site (CP-318). On Plug this is the homepage: / renders it
   through En, which switches on PLATFORM.

   Its own paths are /en, /zh and /cn, and they are what LANGUAGE_PATHS in
   PlugLanding.js switches between. They are plain Routes registered only on
   Plug, deliberately: /En and friends go through HomePageRoute, which reads
   the first two characters of a path as a language code and redirects when
   they disagree with the session's — which sends a language switch straight
   back to the browser's own language. Matching is case-insensitive and these
   come first in the Switch, so on Plug they also take /En, /Zh and /Cn.

   /PlugEn, /PlugZh and /PlugCn stay as aliases — plug-impact's static export
   resolves to them — but nothing in the app navigates there any more. They
   are plain Routes for the same reason, and also because HomePageRoute would
   bounce /PlugEn to /enugEn. They are registered only on Plug too: a
   CheckPlus build served the whole marketing site at check.plus/PlugEn
   otherwise.

   The marketplace keeps its own path, /webapp. */
import PlugEn from "./app/PlugEn/PlugEn";
import PlugZh from "./app/PlugZh/PlugZh";
import PlugCn from "./app/PlugCn/PlugCn";

/* Staff Account */
import AdminLogin from "./app/AdminLogin/AdminLogin";
import AdminLoginPassword from "./app/AdminLoginPassword/AdminLoginPassword";
import Admin from "./app/Admin/Admin";

/* Generic system response pages */
import ErrorPrompt from "./app/ErrorPrompt/ErrorPrompt";
import DonePrompt from "./app/DonePrompt/DonePrompt";

const Routes = ({ component: Component, ...rest }) => (
  <BrowserRouter>
    <AuthProvider>
      <Switch>
        {/* Homepages in English */}
        <PublicRoute exact path="/" component={En} />

        {/* The Plug marketing site's own language paths, ahead of the
            HomePageRoute entries they shadow. See the import block. */}
        {PLATFORM === "plug" && [
          <Route exact path="/en" key="plug-en" component={PlugEn} />,
          <Route exact path="/zh" key="plug-zh" component={PlugZh} />,
          <Route exact path="/cn" key="plug-cn" component={PlugCn} />,
        ]}

        <HomePageRoute path="/En" component={En} />
        <HomePageRoute path="/EnVideo" component={EnVideo} />
        <HomePageRoute path="/EnTeam" component={EnTeam} />
        <HomePageRoute path="/EnEdward" component={EnEdward} />
        <HomePageRoute path="/EnRix" component={EnRix} />
        <HomePageRoute path="/EnWhyCheckPlus" component={EnWhyCheckPlus} />
        <HomePageRoute path="/EnContact" component={EnContact} />
        <HomePageRoute path="/EnFaq" component={EnFaq} />
        <HomePageRoute path="/EnPricing" component={EnPricing} />
        <HomePageRoute path="/EnSecurity" component={EnSecurity} />
        <HomePageRoute path="/EnResponsibility" component={EnResponsibility} />
        <HomePageRoute path="/EnSupportHK" component={EnSupportHK} />
        <HomePageRoute path="/EnStripe" component={EnStripe} />
        <HomePageRoute path="/EnPrivacy" component={EnPrivacy} />
        <HomePageRoute path="/EnPrivacyPolicy" component={EnPrivacyPolicy} />
        <HomePageRoute path="/EnTerms" component={EnTerms} />

        {/* Homepages in Traditional Chinese */}
        <HomePageRoute path="/Zh" component={Zh} />
        <HomePageRoute path="/ZhVideo" component={ZhVideo} />
        <HomePageRoute path="/ZhTeam" component={ZhTeam} />
        <HomePageRoute path="/ZhEdward" component={ZhEdward} />
        <HomePageRoute path="/ZhRix" component={ZhRix} />
        <HomePageRoute path="/ZhWhyCheckPlus" component={ZhWhyCheckPlus} />
        <HomePageRoute path="/ZhContact" component={ZhContact} />
        <HomePageRoute path="/ZhFaq" component={ZhFaq} />
        <HomePageRoute path="/ZhPricing" component={ZhPricing} />
        <HomePageRoute path="/ZhSecurity" component={ZhSecurity} />
        <HomePageRoute path="/ZhResponsibility" component={ZhResponsibility} />
        <HomePageRoute path="/ZhSupportHK" component={ZhSupportHK} />
        <HomePageRoute path="/ZhStripe" component={ZhStripe} />
        <HomePageRoute path="/ZhPrivacy" component={ZhPrivacy} />
        <HomePageRoute path="/ZhPrivacyPolicy" component={ZhPrivacyPolicy} />
        <HomePageRoute path="/ZhTerms" component={ZhTerms} />

        {/* Homepages in Simplified Chinese */}
        <HomePageRoute path="/Cn" component={Cn} />
        <HomePageRoute path="/CnVideo" component={CnVideo} />
        <HomePageRoute path="/CnTeam" component={CnTeam} />
        <HomePageRoute path="/CnEdward" component={CnEdward} />
        <HomePageRoute path="/CnRix" component={CnRix} />
        <HomePageRoute path="/CnWhyCheckPlus" component={CnWhyCheckPlus} />
        <HomePageRoute path="/CnContact" component={CnContact} />
        <HomePageRoute path="/CnFaq" component={CnFaq} />
        <HomePageRoute path="/CnPricing" component={CnPricing} />
        <HomePageRoute path="/CnSecurity" component={CnSecurity} />
        <HomePageRoute path="/CnResponsibility" component={CnResponsibility} />
        <HomePageRoute path="/CnSupportHK" component={CnSupportHK} />
        <HomePageRoute path="/CnStripe" component={CnStripe} />
        <HomePageRoute path="/CnPrivacy" component={CnPrivacy} />
        <HomePageRoute path="/CnPrivacyPolicy" component={CnPrivacyPolicy} />
        <HomePageRoute path="/CnTerms" component={CnTerms} />

        {/* Organization Account */}
        {/* New Entry Point for Org Registration */}
        <PublicRoute
          path="/org/register/select-plan"
          component={OrgPlanSelection}
        />
        {/* Route for Plan Builder */}
        <PublicRoute
          path="/org/register/build-plan"
          component={OrgPlanBuilder}
        />

        <OrgLoginRoute path="/OrgLogin" component={OrgLogin} />
        <OrgLoginRoute path="/OrgLoginPassword" component={OrgLoginPassword} />
        {/* OrgSignup might be the next step after plan selection */}
        <OrgLoginRoute path="/OrgSignup" component={OrgSignup} />
        <OrgLoginRoute path="/OrgAgreement" component={OrgAgreement} />
        {/* OrgRegisterInfo might also be a potential next step */}
        <OrgLoginRoute path="/OrgRegisterInfo" component={OrgRegisterInfo} />
        <OrgLoginRoute path="/OrgConfirm" component={OrgConfirm} />
        <PublicRoute path="/OrgForgotPassword" component={OrgForgotPassword} />
        <PublicRoute
          path="/OrgForgotPasswordConfirm"
          component={OrgForgotPasswordConfirm}
        />
        <PublicRoute path="/UsernameConfirm" component={UsernameConfirm} />
        <ProtectedOrgRoute path="/OrgSetup" component={OrgSetup} />
        <ProtectedOrgRoute
          path="/AccountOverview"
          component={AccountOverview}
        />
        <ProtectedOrgRoute path="/CreateGoal" component={CreateGoal} />
        <ProtectedOrgRoute
          path="/AddOfflinePayments"
          component={AddOfflinePayments}
        />
        <ProtectedOrgRoute path="/OrgSettings" component={OrgSettings} />
        <StripeRoute path="/OrgBankAdd" component={OrgBankAdd} />
        <ProtectedOrgRoute path="/OrgBankInfo" component={OrgBankInfo} />
        <ProtectedOrgRoute path="/OrgDecline" component={OrgDecline} />
        <ProtectedOrgRoute path="/PayoutSuccess" component={PayoutSuccess} />
        <PublicRoute path="/EmailVerified" component={EmailVerified} />
        <PublicRoute path="/ResetEmail" component={ResetEmail} />
        <PublicRoute
          path="/DonorEmailVerified"
          component={DonorEmailVerified}
        />
        <PublicRoute
          path="/EmailResetPassword"
          component={EmailResetPassword}
        />

        {/* Plug Marketplace, served at /webapp */}
        <PublicRoute path="/webapp" component={Marketplace} />

        {/* The path it used to answer on. Kept because it is not only ours to
            change: the backend builds Stripe's 3DS return_url as
            `<origin>/Marketplace` for Plug hosts (checkplus-backend
            routes/donors/cards.js, CP-296), so a card that hits a 3DS
            challenge comes back here. Stripe appends setup_intent and
            redirect_status to that URL, so the query has to survive the
            redirect or the card-saving flow loses its confirmation. */}
        <Route
          path="/Marketplace"
          render={({ location }) => (
            <Redirect to={{ pathname: "/webapp", search: location.search }} />
          )}
        />

        {/* Aliases the static export resolves to. Registered only on Plug,
            like the language paths above: a CheckPlus build has no use for
            them, and leaving them in served the Plug marketing site from
            check.plus/PlugEn — unlinked, but public and crawlable. */}
        {PLATFORM === "plug" && [
          <Route path="/PlugEn" key="plug-alias-en" component={PlugEn} />,
          <Route path="/PlugZh" key="plug-alias-zh" component={PlugZh} />,
          <Route path="/PlugCn" key="plug-alias-cn" component={PlugCn} />,
        ]}

        {/* Staff Account */}
        <AdminLoginRoute path="/AdminLogin" component={AdminLogin} />
        <AdminLoginRoute
          path="/AdminLoginPassword"
          component={AdminLoginPassword}
        />
        <AdminProtectedRoute path="/Admin" component={Admin} />

        {/* Generic system response pages */}
        <PublicRoute path="/ErrorPrompt" component={ErrorPrompt} />
        <PublicRoute path="/DonePrompt" component={DonePrompt} />
        <PublicRoute path="/404" component={ErrorPrompt} />
        <PublicRoute path="*" exact={true} component={ErrorPrompt} />
      </Switch>
    </AuthProvider>
  </BrowserRouter>
);

Routes.propTypes = {
  component: PropTypes.func,
};

export default Routes;
